<template>
  <div class="chat-widget">
    <div class="chat-main">
      <!-- 左侧联系人列表 -->
      <aside class="contacts-panel">
        <div class="contacts-header">
          <div v-if="uid" class="uid-block" :title="uid">
            <span class="uid-label">我的UID</span>
            <span class="uid-value">{{ uid }}</span>
            <span class="uid-label">我的用户名</span>
            <span class="uid-value">{{ cachedWebsiteUserName }}</span>
          </div>
          <span class="contacts-header-title">最近联系人</span>
        </div>
        <div class="contacts-list">
          <div
            v-for="c in sortedContacts"
            :key="c.key"
            :class="['contact-item', { active: c.key === activeContactKey }]"
            @click="selectContact(c.key)"
          >
            <div class="contact-avatar" :style="{ background: c.avatarColor }">
              <span class="avatar-text">{{ c.avatarText }}</span>
              <span v-if="c.unread" class="unread-dot"></span>
            </div>
            <div class="contact-info">
              <div class="contact-row">
                <span class="contact-name">{{ c.name }}</span>
                <span class="contact-time">{{ formatTime(c.lastMsgTime) }}</span>
              </div>
              <div class="contact-preview">{{ c.lastMsgText || '暂无消息' }}</div>
            </div>
          </div>
          <div v-if="sortedContacts.length === 0" class="contacts-empty">暂无联系人</div>
        </div>
      </aside>

      <!-- 右侧聊天区 -->
      <section class="chat-panel">

        <div class="chat-header">
          <div class="header-content">
            <span class="company-name">{{ activeContact ? headerTitle : '' }}</span>
            <small v-if="activeContact" class="header-sub">
              {{ activeContact.type === 'admin' ? '官方客服' : ('商户ID: ' + activeContact.merchantId) }}
            </small>
          </div>
          <!-- TODO: 发布前需注释! -->
          <!-- <button class="platform-login-btn" @click="platformLogin" title="[访客登陆平台]">模拟登陆平台</button> -->
          <button class="close-btn" @click="hideChat" title="关闭">×</button>
        </div>

        <div class="chat-body" ref="chatBody">
          <template v-if="activeContact">
            <div
              v-for="(msg, idx) in activeContact.messages"
              :key="msg.msgUuid || msg.messageId || idx"
              :class="['chat-message', isSelfMsg(msg) ? 'msg-self' : 'msg-other']"
            >
              <div class="msg-avatar" :style="{ background: msgAvatarColor(msg) }">
                {{ msgAvatarText(msg) }}
              </div>
              <div class="msg-bubble-wrap">
                <div class="msg-meta">
                  <span class="msg-sender">{{ msg.senderName || senderLabel(msg.senderRole) }}</span>
                  <span v-if="msg.senderRole === 200" class="merchant-id">{{ msg.senderId }}</span>
                  <span class="msg-time">{{ formatDate(msg.sendTimeStr) }}</span>
                </div>
                <div class="msg-bubble">{{ msg.content }}</div>
              </div>
            </div>
          </template>
          <div v-else class="empty-tip">请在左侧选择联系人开始聊天</div>
        </div>

        <div class="tools-container" ref="emojiArea">
          <button class="tool-btn" @click.stop="toggleEmojiPicker" title="表情">😊</button>
          <span class="char-counter" :class="{ 'char-counter-warn': message.length >= maxLen }">
            {{ message.length }}/{{ maxLen }}
          </span>
          <transition name="emoji-pop">
            <div v-if="showEmojiPicker" class="emoji-picker" ref="emojiPicker" @mousedown.stop>
              <div class="emoji-picker-header">
                <span class="emoji-picker-title">😀 表情</span>
                <span class="emoji-picker-tip">点击插入</span>
              </div>
              <div class="emoji-picker-grid">
                <span
                  v-for="e in emojis"
                  :key="e"
                  class="emoji-item"
                  @click="insertEmoji(e)"
                >{{ e }}</span>
              </div>
              <span class="emoji-picker-arrow"></span>
            </div>
          </transition>
        </div>
        <div class="input-container">
          <textarea
            v-model="message"
            @keydown="handleKeydown"
            :maxlength="maxLen"
            placeholder="输入消息... (Enter 发送, Ctrl+Enter 换行)"
            class="chat-input"
            :disabled="!activeContact"
          ></textarea>
        </div>
        <div class="send-container">
          <button @click="sendMessage" class="send-btn" :disabled="!activeContact || !message">发送(Enter)</button>
        </div>
      </section>
    </div>

    <!-- 耗时操作 loading 遮罩, 由 loadingStack 驱动, 栈顶提示语显示在 loadingTip -->
    <transition name="loading-fade">
      <div v-if="loadingTip" class="loading-overlay">
        <div class="loading-box">
          <div class="loading-spinner"></div>
          <div class="loading-text">{{ loadingTip }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import messagesService from '@/services/messages';
import userService from '@/services/user';
import visitorService from '@/services/visitor';
import WebSocketService from '@/services/websocketService'; // 引入 WebSocketService
import { v4 as uuidv4 } from 'uuid';

const ADMIN_KEY = 'admin';
const ADMIN_NAME = '网站管理员';
const ADMIN_COMPANY = '上海创族科技有限公司';
// 与后端 model.UidAdmin 保持一致, 用于访客 -> 网站管理员 的 receiverId.
const ADMIN_UID = '00016CAF-E585-9A93-834F-B9FEA0AAC577';
const ROLE_ADMIN = 100;
const ROLE_MERCHANT = 200;
const ROLE_USER = 1000;
const AVATAR_COLORS = ['#5B8FF9', '#5AD8A6', '#F6BD16', '#E86452', '#6DC8EC', '#945FB9', '#FF9D4D', '#269A99'];
const EMOJIS = [
  // 表情 / 情绪
  '😀','😁','😂','🤣','😃','😄','😅','😆','😉','😊','😋','😎','😍','🥰','😘','😗','😙','😚','🙂','🤗',
  '🤩','🤔','🤨','😐','😑','😶','🙄','😏','😣','😥','😮','🤐','😯','😪','😫','🥱','😴','😌','😛','😜',
  '😝','🤤','😒','😓','😔','😕','🙃','🤑','😲','☹️','🙁','😖','😞','😟','😤','😢','😭','😦','😧','😨',
  '😩','🤯','😬','😰','😱','🥵','🥶','😳','🤪','😵','🥴','😠','😡','🤬','😷','🤒','🤕','🤢','🤮','🥳',
  '😇','🤠','🥺','🤡','🤥','🤫','🤭','🧐','🥲','🤓','😈','👿','💀','👻','👽','🤖','💩','🙈','🙉','🙊',
  // 手势 / 身体
  '👍','👎','👌','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','👇','☝️','✋','🤚','🖐️','🖖','👋','💪','🦾',
  '🙏','👏','🙌','🤝','👀','👁️','👂','👃','👄','💋',
  // 心 / 爱
  '❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟','♥️',
  // 庆祝 / 礼物
  '🎉','🎊','🎁','🎈','🎀','🎂','🍰','🧁','🍭','🍬','🍫','🍩','🍪','🥂','🍾','🍻','🍺','🍷','🥃','🍸',
  '🏆','🥇','🥈','🥉','🎯','🎮','🎲','🎰','🪅','🪩',
  // 花 / 植物 / 自然
  '🌹','🌺','🌻','🌼','🌷','💐','🥀','🌸','🪷','🌱','🌿','☘️','🍀','🍁','🍂','🍃','🪴','🌵','🌴','🌳',
  '🌲','🌾','💮','🌍','🌎','🌏','🌋','🏔️','🏖️','🏝️',
  // 天气 / 天体
  '☀️','🌤️','⛅','🌥️','☁️','🌦️','🌧️','⛈️','🌩️','🌨️','❄️','☃️','⛄','🌬️','💧','🌊','🌈','☔','⚡','🔥',
  '✨','⭐','🌟','💫','💥','💦','💨','🌙','🌛','🌜','🌚','🌕','🌖','🌗','🌘','🌑','🌒','🌓','🌔','🪐',
  // 水果 / 食物
  '🍎','🍏','🍐','🍊','🍋','🍌','🍉','🍇','🍓','🫐','🍈','🍒','🍑','🥭','🍍','🥥','🥝','🍅','🍆','🥑',
  '🥦','🥬','🥒','🌶️','🫑','🌽','🥕','🧄','🧅','🥔','🍠','🥐','🥯','🍞','🥖','🥨','🧀','🥚','🍳','🥞',
  '🥓','🥩','🍗','🍖','🍔','🍟','🍕','🌭','🥪','🌮','🌯','🥙','🧆','🥗','🍿','🍱','🍙','🍚','🍛','🍜',
  '🍝','🍢','🍣','🍤','🍥','🥮','🍡','🥟','🍦','🍨','🥛','☕','🫖','🍵','🍶','🍯',
  // 动物
  '🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐨','🐯','🦁','🐮','🐷','🐽','🐸','🐵','🐒','🐔','🐧','🐦',
  '🐤','🦆','🦉','🦇','🐺','🐗','🐴','🦄','🐝','🐛','🦋','🐌','🐞','🐢','🐍','🦖','🐙','🦀','🐬','🐳',
  // 物品 / 科技
  '📱','💻','⌨️','🖥️','🖨️','🖱️','💽','💾','💿','📀','📷','📹','🎥','📞','📺','📻','🎙️','🔔','🔕','📌',
  '📍','📎','🖇️','📏','📐','✂️','🔒','🔓','🔑','🗝️','🔨','⛏️','🛠️','🔧','💡','🔦','🕯️','💎','⏰','⌛'
];

function pickColor(seed) {
  let h = 0;
  const s = String(seed || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function timeOf(msg) {
  if (!msg) return Date.now();
  if (msg.sendTimeMs) return msg.sendTimeMs;
  if (msg.sendTimeStr) {
    const t = new Date(msg.sendTimeStr).getTime();
    if (!isNaN(t)) return t;
  }
  return Date.now();
}

export default {
  data() {
    return {
      init_event_type: "onlinekefu-user:init",
      bind_event_type: "onlinekefu-user:bind",
      uid: "",
      websiteUserId: "",
      cachedWebsiteRealName: "",
      cachedWebsiteUserName: "",
      token: "",
      role: 1000,
      merchantID: "",
      message: "",
      websocketService: null,
      contacts: {}, // { key: { key,type,merchantId,name,avatarText,avatarColor,messages,lastMsgText,lastMsgTime,unread } }
      activeContactKey: "",
      showEmojiPicker: false,
      emojis: EMOJIS,
      maxLen: 500,
      originalTitle: typeof document !== 'undefined' ? (document.title || '在线客服') : '在线客服',
      titleFlashTimer: null,
      pageVisible: typeof document !== 'undefined' ? !document.hidden : true,
      // 标题栏闪烁计数: 与联系人 unread 状态解耦, 任何收到的对方消息都自增,
      // 由用户回到页面 / 切换联系人 / 发送消息 等交互时清零, 以保证当前打开的联系人收到消息也能闪烁.
      alertCount: 0,
      // 耗时操作提示栈: showLoading 入栈, hideLoading 出栈, 嵌套调用时不会被里层提前关闭.
      loadingStack: [],
    };
  },
  computed: {
    loadingTip() {
      const s = this.loadingStack;
      return s.length > 0 ? s[s.length - 1] : '';
    },
    sortedContacts() {
      const list = Object.values(this.contacts);
      return list.sort((a, b) => {
        if (a.type === 'admin') return -1;
        if (b.type === 'admin') return 1;
        return (b.lastMsgTime || 0) - (a.lastMsgTime || 0);
      });
    },
    activeContact() {
      return this.contacts[this.activeContactKey] || null;
    },
    headerTitle() {
      if (this.activeContact) {
        if (this.activeContact.type === 'admin') return ADMIN_COMPANY;
        // 优先使用后端 peerName (商户公司名); 缓存未命中时回退为 "商户 "+id.
        return this.activeContact.name || ('商户 ' + this.activeContact.merchantId);
      }
      return ADMIN_COMPANY;
    },
    totalUnread() {
      return Object.values(this.contacts).reduce((n, c) => n + (c.unread ? 1 : 0), 0);
    },
  },
  watch: {
    // 用 alertCount 替代原先 totalUnread 触发的标题闪烁: 后者在"接收方就是当前选中联系人且页面可见"时
    // 不会自增, 导致标题栏不闪. 改由 alertCount 直接驱动可覆盖该场景.
    alertCount(n) {
      this.updateTitle(n);
    },
    activeContactKey(key) {
      const c = this.contacts[key];
      if (c) {
        c.unread = false;
        this.merchantID = c.type === 'merchant' ? c.merchantId : '';
        this.role = c.type === 'merchant' ? 1100 : 1200;
        this.alertCount = 0; // 切换联系人视为用户感知, 清除标题栏闪烁计数
        this.$nextTick(() => this.scrollToEnd());
      }
    },
  },
  created() {
    window.addEventListener('message', this.receiveMessage, false);
    document.addEventListener('visibilitychange', this.handleVisibility);
    window.addEventListener('focus', this.handleVisibility);
    document.addEventListener('mousedown', this.handleDocMousedown);
  },
  beforeDestroy() {
    window.removeEventListener('message', this.receiveMessage, false);
    document.removeEventListener('visibilitychange', this.handleVisibility);
    window.removeEventListener('focus', this.handleVisibility);
    document.removeEventListener('mousedown', this.handleDocMousedown);
    this.clearTitleFlash();
  },
  mounted() {
    // URL 参数处理: 测试用, 上线前删除
    // const urlParams = new URLSearchParams(window.location.search);
    // let testcase = null;
    // for (const [key, value] of urlParams.entries()) {
    //   if (key.toLowerCase() === 'testcase') { testcase = value; break; }
    // }
    // let test_merchant_id = null;
    // for (const [key, value] of urlParams.entries()) {
    //   if (key.toLowerCase() === 'test_merchant_id') { test_merchant_id = value; break; }
    // }
    // if (testcase) {
    //   // TODO: 正式发布时必须删除!!!
    //   if (testcase == 10) { // 仅打开聊天联系人
    //     setTimeout(() => {
    //       this.receiveMessage({ data: {
    //         type: this.init_event_type,
    //         role: 1000,
    //       }});
    //     }, 30);
    //   } else if (testcase == 20) { // 商户
    //     setTimeout(() => {
    //       this.receiveMessage({ data: {
    //         type: this.init_event_type,
    //         role: 1100,
    //         merchant_id: test_merchant_id || "test-merchant_id1"
    //       }});
    //     }, 30);
    //   } else { // 网站客服
    //     setTimeout(() => {
    //       this.receiveMessage({ data: {
    //         type: this.init_event_type,
    //         role: 1200,
    //       }});
    //     }, 30);
    //   }
    // }
  },
  updated() {
    this.scrollToEnd();
  },

  methods: {
    // 主站通过 postMessage 向 iframe 发送 json, 告诉 iframe 用户点击了商户[商户ID]还是网站客服.
    receiveMessage(event) {
      console.log("receiveMessage, event:", event);
      if (!event || !event.data) return;
      try {
        let data = null;
        console.log("receiveMessage, event.data:", event.data);
        if (typeof event.data === 'string') {
          data = JSON.parse(event.data);
        }
        console.log("receiveMessage, data", data);
        if (data.type==null) {
          console.log("receiveMessage, data.type==null");
          return;
        }
        if (data.type == this.init_event_type) {
          this.initEventProcess(data);
        } else if (data.type == this.bind_event_type) {
          this.bindEventProcess(data);
        }
        
        this.login();
      } catch (e) {
        console.error("receiveMessage, error:", e);
      }
    },
    platformLogin() { // 模拟访客在平台登陆了
      setTimeout(() => {
        this.receiveMessage({ data: {
          type: this.bind_event_type,
          customer_website_user_token: "customerWebsiteUserToken1",
        }});
      }, 30);
    },
    bindEventProcess(data) {
      console.log("-> bindEventProcess, data:", data)
      let customer_website_user_token = data.customer_website_user_token
      if (!customer_website_user_token || customer_website_user_token.length < 1) return;
      localStorage.setItem('customer_website_user_token', customer_website_user_token);
      this.tryBindVisitor();
    },
    initEventProcess(data) {
        if (data.role && data.role > 0) {
          console.log("-> initEventProcess, data:", data)
          this.role = data.role;
          
          if (this.role>=1000 && this.role<1200) { // 点击了和商户沟通
            console.log("receiveMessage, this.role:", this.role);
            console.log("receiveMessage, data.merchant_id:", data.merchant_id);
            if (data.merchant_id && data.merchant_id.length > 0) {
              // 用户点击了商户: 不主动建管理员联系人, 待 loadMessages 或后续收到管理员消息时再加入
              this.merchantID = data.merchant_id;
              this.ensureContact('merchant', data.merchant_id, data.merchantName);
              this.activeContactKey = `merchant:${data.merchant_id}`;
              
              // 如果用户打开在线客服之前已经登陆了, 再直接保存登陆信息
              let customer_website_user_token = data.customer_website_user_token
              if (customer_website_user_token && customer_website_user_token.length >0) {
                localStorage.setItem('customer_website_user_token', customer_website_user_token);
              }
            }
          } else if (this.role==1200) {// 点击了和网站客服沟通
            console.log("receiveMessage, this.role:", this.role);

            // 用户点击了网站客服: 此时管理员需要存在并被选中
            this.ensureContact('admin'); 
            this.activeContactKey = ADMIN_KEY;
              
            // 如果用户打开在线客服之前已经登陆了, 再直接保存登陆信息
            let customer_website_user_token = data.customer_website_user_token
            if (customer_website_user_token && customer_website_user_token.length >0) {
              localStorage.setItem('customer_website_user_token', customer_website_user_token);
            }
          } else {
              // 网站管理员没有聊天历史并且用户没点击打开管理员聊天图标的情况下，默认不显示。 如果有聊天历史的话才需要置顶。
          }
        } else {
          this.role = 1000;
        }
        if (this.role >= 1000) {
          this.loadMessages();
        }
    },

    // 耗时操作 loading 工具: 以栈管理嵌套调用, 栈顶提示语显示于 loadingTip.
    // 调用方需配合 try/finally, 保证异常路径也能出栈.
    showLoading(tip) {
      this.loadingStack.push(tip || '加载中...');
    },
    hideLoading() {
      this.loadingStack.pop();
    },

    async login() {
      this.showLoading('正在登录...');
      try {
        let rsp = await userService.login();
        if (rsp && rsp.uid && rsp.token) {
          this.uid = rsp.uid;
          this.token = rsp.token;
          if (this.token !== '') {
            this.connectWebSocket();
            if (this.role === 1100 || this.role === 1200) this.loadMessages();
            this.tryBindVisitor();
          }
        } else {
          console.error("登录失败, rsp.uid or rsp.token is null");
        }
      } catch (error) {
        console.error("登录失败:", error);
      } finally {
        this.hideLoading();
      }
    },

    async tryBindVisitor() {
      // 早退守卫前置: 仅在确实要发起 HTTP 时才显示 loading, 避免无操作也弹遮罩.
      if (this.token==null || this.token.length <1) {
        console.log("tryBindVisitor, not login, skip")
        return;
      }
      const ocsToken = localStorage.getItem('ocs_user_token');
      const customerWebsiteUserToken = localStorage.getItem('customer_website_user_token');
      if (!ocsToken || !customerWebsiteUserToken) return;
      this.showLoading('正在绑定账号...');
      try {
        let rsp = await visitorService.bindVisitor();
        if (!rsp) return;
        this.websiteUserId = rsp.websiteUserId
        this.cachedWebsiteRealName = rsp.cachedWebsiteRealName
        this.cachedWebsiteUserName = rsp.cachedWebsiteUserName
        // 绑定成功后, 服务端已把 (visitor_id) 维度的老会话回迁/合并到 (website_user_id) 维度,
        // 本地 contact 的 conversationId 与历史消息可能已对应到新的会话: 重新拉一次列表 + 强制
        // 重载当前 active 联系人的历史, 让登陆前在本浏览器以及其它浏览器留下的消息都可见.
        await this.loadMessages();
        const active = this.contacts[this.activeContactKey];
        if (active && active.conversationId) {
          active.messages = [];
          await this.loadMessagesForContact(active);
        }
      } catch (e) {
        console.error('tryBindVisitor, error:', e);
      } finally {
        this.hideLoading();
      }
    },

    connectWebSocket() {
      const wsPath = `/api/customer/app/ws`;
      let url = "";
      if (process.env.VUE_APP_WS_API_URL) {
        url = process.env.VUE_APP_WS_API_URL + wsPath;
      } else {
        const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsHost = window.location.hostname;
        const wsPort = window.location.port ? `:${window.location.port}` : '';
        url = `${wsProtocol}//${wsHost}${wsPort}${wsPath}`;
      }
      this.websocketService = new WebSocketService(
        url,
        this.token,
        this.handleWebSocketMessage,
        this.handleWebSocketClose,
        this.handleWebSocketError
      );
      this.websocketService.connectWebSocket();
      console.log(`#### connecting WebSocket, url:`, url);
    },

    handleWebSocketMessage(msg) {
      if (msg.msgType === "Notify") return;
      if (msg.msgType === "Ping" || msg.msgType === "Pong") return;
      // CustomerUpdate: 服务端在访客绑定成功后主动推送, 用以触发与 tryBindVisitor 后置处理
      // 一致的刷新流程 —— 重拉联系人元数据 + 清空 active 联系人本地缓存后重拉历史,
      // 让本浏览器 / 其它浏览器在登录前留下的消息合并显示, 无需 F5.
      if (msg.msgType === 'CustomerUpdate') {
        this.refreshAfterCustomerUpdate();
        return;
      }
      this.appendMessage(msg, true);
    },

    async refreshAfterCustomerUpdate() {
      this.showLoading('正在同步消息...');
      try {
        await this.loadMessages();
        const active = this.contacts[this.activeContactKey];
        if (active && active.conversationId) {
          active.messages = [];
          await this.loadMessagesForContact(active);
        }
      } catch (e) {
        console.error('refreshAfterCustomerUpdate, error:', e);
      } finally {
        this.hideLoading();
      }
    },

    handleWebSocketClose(event) {
      console.log(`#### WebSocket closed, event=${event}`);
    },

    handleWebSocketError(event) {
      console.log(`#### WebSocket error, event=${event}`);
    },

    // 拉取会话元数据(不带消息), 渲染左侧"最近联系人"列表.
    // 消息按需在 selectContact / 初始 active 选定时通过 loadMessagesForContact 拉取.
    async loadMessages() {
      if (this.token === '') return;
      this.showLoading('正在加载会话...');
      try {
        let convs = await messagesService.getConversations();
        if (!Array.isArray(convs)) {
          console.error('convs is not an array');
          return;
        }
        // 仅同步 conversationId / lastMsgPreview 等元数据到本地 contact;
        // 已在本地的 messages 不动, 等点击或初始化时再按需加载.
        // 已读相关字段 (lastMessageId / lastReadMessageId / unreadCount) 由后端动态计算后下发,
        // 这里以"向前推进"语义合并: 取本地与服务端的较大值, 防止本轮 HTTP 拉取覆盖 WS 增量已经更新过的本地状态.
        convs.forEach(conv => {
          if (!conv || !conv.peerRole) return;
          let c = null;
          if (conv.peerRole === 100) {
            c = this.ensureContact('admin');
          } else if (conv.peerRole === 200) {
            c = this.ensureContact('merchant', conv.peerId);
          } else {
            return;
          }
          // 商户名 (公司名) 由后端 DisplayNameOf -> GetMerchantInfo 解析后通过 peerName 下发,
          // 这里覆盖本地 fallback ("商户 "+id), 让联系人列表与聊天标题显示真实名称.
          if (conv.peerName) c.name = conv.peerName;
          c.conversationId = conv.conversationId;
          if (conv.lastMessageAtMs && (!c.lastMsgTime || conv.lastMessageAtMs > c.lastMsgTime)) {
            c.lastMsgTime = conv.lastMessageAtMs;
            c.lastMsgText = conv.lastMessagePreview || c.lastMsgText || '';
          }
          const srvLastMsgId = Number(conv.lastMessageId || 0);
          const srvLastReadId = Number(conv.lastReadMessageId || 0);
          const srvUnread = Number(conv.unreadCount || 0);
          c.lastMessageId = Math.max(c.lastMessageId || 0, srvLastMsgId);
          c.lastReadMessageId = Math.max(c.lastReadMessageId || 0, srvLastReadId);
          // 未读数: 若本地已通过 WS 看到更新的消息 (本地 lastMessageId > 服务端值), 沿用本地 unread 状态;
          // 否则采信服务端结果.
          c.unreadCount = (c.lastMessageId > srvLastMsgId) ? (c.unreadCount || 0) : srvUnread;
          c.unread = c.unreadCount > 0;
        });
        // 选定初始 active contact: 角色 1100 优先选商户, 其它选管理员.
        if (!this.activeContactKey || !this.contacts[this.activeContactKey]) {
          if (this.role === 1100 && this.merchantID) {
            this.activeContactKey = `merchant:${this.merchantID}`;
          } else {
            this.activeContactKey = ADMIN_KEY;
          }
        }
        // 为当前 active contact 拉取最近 300 条消息(若已有本地消息则保留, 避免覆盖 WS 增量).
        const active = this.contacts[this.activeContactKey];
        if (active && (!active.messages || active.messages.length === 0)) {
          this.loadMessagesForContact(active);
        }
        // 对仍是 fallback 名 ("商户 "+id) 的商户联系人补一次主动解析:
        // 适用于通过 URL 参数直接打开某商户聊天但还没产生 ocs_conversation 的场景,
        // 此时 /conversations 返回空, 上面的 forEach 不会触发 peerName 覆盖.
        this.resolveMerchantNames();
      } finally {
        this.hideLoading();
      }
    },

    // 拉取所有 fallback 名字的商户联系人对应公司名 (仅 companyName, 不含 userName).
    // 失败静默, 让 UI 继续显示 fallback. 主站传过来或者已解析过的不重复请求.
    async resolveMerchantNames() {
      if (this.token === '') return;
      const targets = Object.values(this.contacts).filter(c =>
        c && c.type === 'merchant' && c.merchantId && c.name === ('商户 ' + c.merchantId)
      );
      for (const c of targets) {
        const info = await messagesService.getMerchantInfo(c.merchantId);
        if (info && info.companyName) c.name = info.companyName;
      }
    },

    // 拉取指定 contact 对应会话的最近 300 条消息, 合并入 contact.messages.
    // conversationId 为 0 (尚未与对端建立会话)时跳过.
    // 合并而非整体替换的原因: HTTP 拉取与 WebSocket 推送之间存在窗口期 ——
    // 切换/打开联系人发起 HTTP 拉取的同时, 该联系人的新消息可能已通过 WS 先行 push 到本地;
    // 若直接用服务端结果整体覆盖, 这条 WS 消息会从视图中"消失", 直到再次切换联系人才会出现.
    async loadMessagesForContact(contact) {
      if (!contact || !contact.conversationId) return;
      this.showLoading('正在加载消息...');
      try {
        const msgs = await messagesService.getMessagesByConversation(contact.conversationId, 300);
        const fetched = Array.isArray(msgs) ? msgs : [];
        const local = Array.isArray(contact.messages) ? contact.messages : [];
        // 以服务端结果为基准, 再补回本地有但服务端尚未返回的消息. 双键去重:
        //   - messageId: 已落库消息 (HTTP / WS 推送都带), 跨拉取窗口最稳定;
        //   - msgUuid  : 本地刚乐观渲染但尚未拿到 messageId 的消息 (本端 sendMessage 路径).
        // 旧 ocs_message 行可能没有 msgUuid, 单 messageId 维度仍能正确去重.
        const seenIds = new Set();
        const seenUuids = new Set();
        fetched.forEach(m => {
          if (m.messageId) seenIds.add(m.messageId);
          if (m.msgUuid) seenUuids.add(m.msgUuid);
        });
        const extras = local.filter(m => {
          if (!m) return false;
          if (m.messageId && seenIds.has(m.messageId)) return false;
          if (m.msgUuid && seenUuids.has(m.msgUuid)) return false;
          return true;
        });
        const merged = fetched.concat(extras);
        merged.sort((a, b) => timeOf(a) - timeOf(b));
        contact.messages = merged;
        // 用最后一条消息刷新预览
        const last = contact.messages.length > 0 ? contact.messages[contact.messages.length - 1] : null;
        if (last) {
          contact.lastMsgText = last.content || contact.lastMsgText || '';
          contact.lastMsgTime = timeOf(last);
        }
        if (contact.key === this.activeContactKey) {
          this.$nextTick(() => this.scrollToEnd());
        }
      } catch (e) {
        console.error('loadMessagesForContact error:', e);
      } finally {
        this.hideLoading();
      }
    },

    ensureContact(type, merchantId, merchantName) {
      const key = type === 'admin' ? ADMIN_KEY : `merchant:${merchantId}`;
      if (this.contacts[key]) return this.contacts[key];
      const c = {
        key,
        type,
        merchantId: type === 'merchant' ? merchantId : '',
        conversationId: 0,
        name: type === 'admin' ? ADMIN_NAME : (merchantName?merchantName:('商户 ' + merchantId)),
        avatarText: type === 'admin' ? '管' : '商',
        avatarColor: type === 'admin' ? '#409eff' : pickColor(merchantId),
        messages: [],
        lastMsgText: '',
        lastMsgTime: 0,
        unread: false,
        // 会话级已读状态字段 (新增): 由后端动态计算并在 loadMessages 同步, WS 增量本地维护.
        lastMessageId: 0,
        lastReadMessageId: 0,
        unreadCount: 0,
      };
      this.$set(this.contacts, key, c);
      return c;
    },

    // 推断这条消息属于访客侧哪一个会话:
    //   - 自己发出的(senderRole=1000): 看 receiverRole/receiverId
    //   - 收到的: 看 senderRole/senderId
    contactKeyOfMsg(msg) {
      const peerRole = msg.senderRole === 1000 ? msg.receiverRole : msg.senderRole;
      const peerId = msg.senderRole === 1000 ? msg.receiverId : msg.senderId;
      if (peerRole === 100) return ADMIN_KEY;
      if (peerRole === 200) return `merchant:${peerId}`;
      return ADMIN_KEY;
    },

    appendMessage(msg, incoming) {
      const key = this.contactKeyOfMsg(msg);
      const c = key === ADMIN_KEY
        ? this.ensureContact('admin')
        : this.ensureContact('merchant', key.slice('merchant:'.length));
      // 客户端去重: 优先按 messageId, 退化按 msgUuid (本地乐观渲染 + 服务端回声场景).
      // 两键任一命中即视为重复, 避免历史拉取与 WS 推送重叠时同一条消息出现两次.
      const dupById = msg.messageId && c.messages.some(m => m.messageId && m.messageId === msg.messageId);
      const dupByUuid = msg.msgUuid && c.messages.some(m => m.msgUuid && m.msgUuid === msg.msgUuid);
      if (!dupById && !dupByUuid) {
        c.messages.push(msg);
      }
      if (msg.conversationId) c.conversationId = msg.conversationId;
      if (msg.messageId && msg.messageId > (c.lastMessageId || 0)) c.lastMessageId = msg.messageId;
      c.lastMsgText = msg.content || '';
      c.lastMsgTime = timeOf(msg);
      if (incoming && msg.senderRole !== 1000) {
        if (key !== this.activeContactKey || !this.pageVisible) {
          c.unread = true;
          // 未读计数: 仅在不满足"自动已读"条件 (非当前会话 / 页面不可见) 时累加.
          c.unreadCount = (c.unreadCount || 0) + 1;
        } else {
          // 当前会话 + 页面可见: 视为用户即时已读, 触发自动已读 (debounce 内合并).
          this.tryAutoMarkRead(c);
        }
        // 标题栏闪烁: 不论该消息是否属于当前激活联系人都自增, 由用户回到页面/切换/发送时清零.
        this.alertCount++;
      }
      if (key === this.activeContactKey) {
        this.$nextTick(() => this.scrollToEnd());
      }
    },

    // tryAutoMarkRead 在满足三项前置条件时, 调度一次"会话已读"上报 (debounce 合并 3000ms):
    //   1) contact 是当前选中联系人 (聊天窗口正在显示);
    //   2) document.visibilityState == 'visible' (页面可见);
    //   3) WebSocket 当前在线.
    // 同时本地立即清零 unreadCount, 避免侧边栏红点闪烁; 服务端最终接受的 lastReadMessageId 取
    // debounce 周期内的最大值.
    tryAutoMarkRead(contact) {
      if (!contact || !contact.conversationId) return;
      if (contact.key !== this.activeContactKey) return;
      if (!this.pageVisible) return;
      if (!this.websocketService || !this.websocketService.isOpen()) return;
      const targetId = Math.max(contact.lastMessageId || 0, contact.lastReadMessageId || 0);
      if (targetId <= (contact.lastReadMessageId || 0)) return; // 没有新进展, 无需上报
      contact.lastReadMessageId = targetId;
      contact.unreadCount = 0;
      contact.unread = false;
      messagesService.scheduleMarkConversationRead(contact.conversationId, targetId);
    },

    selectContact(key) {
      if (key === this.activeContactKey) return;
      this.activeContactKey = key;
      // 切换联系人时, 主动拉取该会话的最近 300 条消息.
      const c = this.contacts[key];
      if (c) {
        this.loadMessagesForContact(c);
        // 会话级已读游标: 仅在存在未读时才上报, 避免无脑请求 (debounce 合并 3000ms).
        // 点击场景不要求 visibility / ws online (用户主动操作即视为已感知).
        if ((c.unreadCount || 0) > 0 && c.conversationId) {
          const targetId = Math.max(c.lastMessageId || 0, c.lastReadMessageId || 0);
          if (targetId > (c.lastReadMessageId || 0)) {
            c.lastReadMessageId = targetId;
            c.unreadCount = 0;
            c.unread = false;
            messagesService.scheduleMarkConversationRead(c.conversationId, targetId);
          }
        }
      }
    },

    isSelfMsg(msg) {
      return msg.senderRole === ROLE_USER;
    },
    senderLabel(role) {
      if (role === ROLE_ADMIN) return ADMIN_NAME;
      if (role === ROLE_MERCHANT) return '商户';
      if (role === ROLE_USER) return '我';
      return '';
    },
    msgAvatarText(msg) {
      if (this.isSelfMsg(msg)) return '我';
      if (msg.senderRole === ROLE_ADMIN) return '管';
      return '商';
    },
    msgAvatarColor(msg) {
      if (this.isSelfMsg(msg)) return '#67c23a';
      if (msg.senderRole === ROLE_ADMIN) return '#409eff';
      return pickColor(msg.senderId);
    },

    scrollToEnd() {
      const chatBody = this.$refs.chatBody;
      if (chatBody) chatBody.scrollTop = chatBody.scrollHeight;
    },

    sendMessage() {
      if (!this.message || !this.activeContact) return;
      const isMerchant = this.activeContact.type === 'merchant';
      const receiverRole = isMerchant ? ROLE_MERCHANT : ROLE_ADMIN;
      const receiverId = isMerchant ? this.activeContact.merchantId : ADMIN_UID;
      const msg = {
        msgType: "text",
        msgUuid: uuidv4(),
        senderRole: ROLE_USER,
        senderId: this.uid,
        receiverRole,
        receiverId,
        content: this.message,
        sendTimeStr: this.getFormattedDate(),
      };
      this.websocketService.sendMessage(msg);
      // 本地乐观渲染: 服务端写库后会回声同一 msgUuid, appendMessage 会按 uuid 去重.
      this.appendMessage(msg, false);
      this.message = '';
      this.showEmojiPicker = false;
      this.alertCount = 0; // 用户主动发送, 视为已感知, 清除标题栏闪烁计数
      this.$nextTick(() => this.scrollToEnd());
    },

    handleKeydown(event) {
      if (event.key === "Enter" && !event.ctrlKey) {
        event.preventDefault();
        this.sendMessage();
      } else if (event.key === "Enter" && event.ctrlKey) {
        event.preventDefault();
        this.message += '\n';
      }
    },

    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    insertEmoji(e) {
      const next = (this.message || '') + e;
      this.message = next.length > this.maxLen ? next.slice(0, this.maxLen) : next;
      // 选中表情后即关闭面板, 与点击外部关闭一致, 避免连续误插入.
      this.showEmojiPicker = false;
    },
    handleDocMousedown(event) {
      if (!this.showEmojiPicker) return;
      const area = this.$refs.emojiArea;
      const picker = this.$refs.emojiPicker;
      const target = event.target;
      if ((area && area.contains(target)) || (picker && picker.contains(target))) return;
      this.showEmojiPicker = false;
    },

    handleVisibility() {
      this.pageVisible = !document.hidden;
      if (this.pageVisible) {
        this.clearTitleFlash();
        this.alertCount = 0; // 用户回到页面, 视为已感知, 清除标题栏闪烁计数
        const c = this.contacts[this.activeContactKey];
        if (c) {
          c.unread = false;
          // 页面切回可见且当前会话有未读: 触发自动已读 (受 debounce 合并保护).
          this.tryAutoMarkRead(c);
        }
      }
    },

    updateTitle(unreadCount) {
      this.clearTitleFlash();
      if (unreadCount > 0) {
        const tip = `【新消息】${this.originalTitle}`;
        let toggle = false;
        document.title = tip;
        this.titleFlashTimer = setInterval(() => {
          document.title = toggle ? this.originalTitle : tip;
          toggle = !toggle;
        }, 1000);
      } else {
        document.title = this.originalTitle;
      }
    },
    clearTitleFlash() {
      if (this.titleFlashTimer) {
        clearInterval(this.titleFlashTimer);
        this.titleFlashTimer = null;
      }
      if (this.originalTitle) document.title = this.originalTitle;
    },

    hideChat() {
      window.parent.postMessage('close-iframe', '*');
    },

    getFormattedDate() {
      return new Date().toISOString();
    },
    formatDate(isoDateString) {
      if (!isoDateString) return '';
      const date = new Date(isoDateString);
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    },
    formatTime(ts) {
      if (!ts) return '';
      const d = new Date(ts);
      const now = new Date();
      const isSameDay = d.getFullYear() === now.getFullYear()
        && d.getMonth() === now.getMonth()
        && d.getDate() === now.getDate();
      if (isSameDay) {
        return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
      }
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${m}/${day}`;
    },
  },
};
</script>

<style scoped>
.chat-widget {
  display: flex;
  flex-direction: column;
  width: 810px;
  height: 610px;
  background: #fff;
  border: 1px solid #e6e8eb;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  font-family: -apple-system, "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
  color: #303133;
  position: relative;
}

/* 聊天面板顶部标题栏 (仅覆盖右侧聊天区) */
.chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(90deg, #409eff, #5b8def);
  color: #fff;
  padding: 10px 14px;
  flex-shrink: 0;
}

.header-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.company-name {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
  color: #fff;
}

.header-sub {
  font-size: 11px;
  opacity: 0.85;
  margin-top: 2px;
  color: #fff;
}

.close-btn {
  background: transparent;
  border: none;
  color: #fff;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
  border-radius: 4px;
  transition: background 0.2s;
}
.close-btn:hover { background: rgba(255, 255, 255, 0.18); }


.platform-login-btn {
  background: rgba(237, 230, 230, 0.18);
  border: none;
  color: #ff0000;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 8px 8px;
  border-radius: 4px;
  transition: background 0.2s;
}
.platform-login-btn:hover { background: #f6f3f3; }

.chat-main {
  flex: 1;
  display: flex;
  min-height: 0;
}

/* 联系人面板 */
.contacts-panel {
  width: 220px;
  border-right: 1px solid #dbdde4;
  background: #f7f8fa;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.contacts-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  background: linear-gradient(90deg, #e2e4e9, #dddfe0);
  color: #5f5858;
  flex-shrink: 0;
  border-bottom: 1px solid #d2d4d9;
}
.contacts-header-title {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1px;
  line-height: 1.2;
}
.uid-block {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 8px;
  margin-bottom: 10px;

  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 6px;
}
.uid-label {
  font-size: 10px;
  letter-spacing: 1px;
  color: #8a8585;
  text-transform: uppercase;
}
.uid-value {
  font-size: 11px;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  color: #5f5858;
  word-break: break-all;
  line-height: 1.35;
}
.contacts-list { flex: 1; overflow-y: auto; }
.contacts-empty {
  text-align: center;
  color: #c0c4cc;
  font-size: 12px;
  padding: 20px 8px;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
  border-left: 6px solid transparent;
  transition: background 0.15s;
}
.contact-item:hover { background: #eef2f8; }
.contact-item.active {
  background: #dcdff0;
  border-left-color: #409eff;
}

.contact-avatar {
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
}
.unread-dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 14px;
  height: 14px;
  background: #f56c6c;
  border-radius: 50%;
  border: 2px solid #f7f8fa;
}
.contact-item.active .unread-dot { border-color: #ffffff; }

.contact-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.contact-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.contact-name {
  font-size: 13px;
  font-weight: 500;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.contact-time {
  font-size: 11px;
  color: #909399;
  flex-shrink: 0;
  margin-left: 6px;
}
.contact-preview {
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 2px;
}

/* 聊天区 */
.chat-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}
.chat-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 14px;
  background: #f5f7fa;
}
.empty-tip {
  text-align: center;
  color: #84868a;
  font-size: 20px;
  padding: 60px 20px;
}

.chat-message {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 12px;
}
.chat-message.msg-self { flex-direction: row-reverse; }

.msg-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.msg-bubble-wrap {
  display: flex;
  flex-direction: column;
  max-width: 70%;
}
.msg-self .msg-bubble-wrap { align-items: flex-end; }
.msg-other .msg-bubble-wrap { align-items: flex-start; }

.msg-meta {
  font-size: 11px;
  color: #909399;
  margin-bottom: 3px;
  display: flex;
  gap: 6px;
  align-items: baseline;
}
.merchant-id { color: #b4afaf; font-size: 0.95em; }
.msg-sender { font-weight: 500; color: #606266; }

.msg-bubble {
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.55;
  background: #ffffff;
  border: 1px solid #ebeef5;
  word-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
  max-width: 100%;
}
.msg-self .msg-bubble {
  background: #d6ecff;
  border-color: #c6e2ff;
  color: #1f4f87;
}

/* 工具栏 / emoji */
.tools-container {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border-top: 1px solid #ebeef5;
  background: #fafbfc;
  position: relative;
}
.tool-btn {
  background: transparent;
  border: none;
  font-size: 18px;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background 0.15s;
}
.tool-btn:hover { background: #eef2f8; }
.char-counter {
  margin-left: auto;
  font-size: 12px;
  color: #909399;
  user-select: none;
}
.char-counter-warn { color: #f56c6c; font-weight: 600; }

/* 浮层式 emoji 选择器, 不再挤压消息列表 */
.emoji-picker {
  position: absolute;
  bottom: calc(100% + 10px);
  left: 8px;
  width: 440px;
  max-height: 280px;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #ffffff 0%, #f4f7fc 100%);
  border: 1px solid rgba(64, 158, 255, 0.18);
  border-radius: 12px;
  z-index: 50;
  overflow: hidden;
  transform-origin: bottom left;
  /* 多层阴影模拟 3D 浮起 */
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    inset 0 -1px 0 rgba(15, 30, 60, 0.04),
    0 2px 4px rgba(15, 30, 60, 0.06),
    0 8px 20px rgba(15, 30, 60, 0.16),
    0 18px 44px rgba(15, 30, 60, 0.18);
}
.emoji-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  font-size: 12px;
  color: #4a5568;
  background: linear-gradient(180deg, #eaf3ff 0%, #d9e8fb 100%);
  border-bottom: 1px solid rgba(64, 158, 255, 0.12);
  letter-spacing: 1px;
  flex-shrink: 0;
}
.emoji-picker-title { font-weight: 600; color: #2978d8; }
.emoji-picker-tip { font-size: 11px; color: #909399; }
.emoji-picker-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 6px 8px 10px;
  overflow-y: auto;
  flex: 1;
}
.emoji-picker-arrow {
  position: absolute;
  bottom: -7px;
  left: 14px;
  width: 12px;
  height: 12px;
  background: #f4f7fc;
  transform: rotate(45deg);
  border-right: 1px solid rgba(64, 158, 255, 0.18);
  border-bottom: 1px solid rgba(64, 158, 255, 0.18);
  box-shadow: 3px 3px 8px rgba(15, 30, 60, 0.08);
}
.emoji-item {
  cursor: pointer;
  font-size: 20px;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: transform 0.12s ease, background 0.15s, box-shadow 0.15s;
  user-select: none;
  line-height: 1;
}
.emoji-item:hover {
  background: linear-gradient(135deg, #eaf2ff, #d7e6ff);
  transform: translateY(-2px) scale(1.12);
  box-shadow: 0 4px 10px rgba(64, 158, 255, 0.25);
}
.emoji-picker-grid::-webkit-scrollbar { width: 6px; }
.emoji-picker-grid::-webkit-scrollbar-thumb { background: #c6d3e6; border-radius: 3px; }
/* 弹出 / 收起 动画 */
.emoji-pop-enter-active, .emoji-pop-leave-active {
  transition: transform 0.18s cubic-bezier(.2,.9,.3,1.2), opacity 0.15s ease;
}
.emoji-pop-enter, .emoji-pop-leave-to {
  opacity: 0;
  transform: translateY(8px) scale(0.94);
}

/* 输入区 */
.input-container {
  display: flex;
  align-items: center;
  padding: 0 8px;
  background: #fff;
}
.chat-input {
  flex-grow: 1;
  padding: 8px 6px;
  border: none;
  resize: none;
  font-size: 14px;
  line-height: 1.5;
  font-family: inherit;
  outline: none;
  height: 60px;
  background: transparent;
}
.chat-input:disabled { background: #f5f7fa; cursor: not-allowed; }

.send-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 6px 10px;
  background: #fff;
  border-top: 1px solid #ebeef5;
}
.send-btn {
  padding: 6px 18px;
  border: none;
  border-radius: 4px;
  background: #409eff;
  color: #fff;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: background 0.2s;
}
.send-btn:hover:not(:disabled) { background: #66b1ff; }
.send-btn:disabled { background: #a0cfff; cursor: not-allowed; }

/* 滚动条美化 */
.contacts-list::-webkit-scrollbar,
.chat-body::-webkit-scrollbar,
.emoji-picker::-webkit-scrollbar { width: 6px; }
.contacts-list::-webkit-scrollbar-thumb,
.chat-body::-webkit-scrollbar-thumb,
.emoji-picker::-webkit-scrollbar-thumb {
  background: #d6dbe0;
  border-radius: 3px;
}

/* 耗时操作 loading 遮罩 */
.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  backdrop-filter: blur(1px);
}
.loading-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 18px 26px;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 6px 20px rgba(15, 30, 60, 0.18);
  min-width: 140px;
}
.loading-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #e4e8ef;
  border-top-color: #409eff;
  border-radius: 50%;
  animation: loading-spin 0.8s linear infinite;
}
.loading-text {
  font-size: 13px;
  color: #303133;
  letter-spacing: 0.5px;
}
@keyframes loading-spin {
  to { transform: rotate(360deg); }
}
.loading-fade-enter-active, .loading-fade-leave-active {
  transition: opacity 0.18s ease;
}
.loading-fade-enter, .loading-fade-leave-to { opacity: 0; }
</style>
